import { Box } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { chartSeries, colors } from '../../const/colors'
import { IDayTotals } from '../../utils/parkingPoolStats'
import { dateIso2localeString } from '../../utils/formatDate'

const WIDTH = 720
const HEIGHT = 220
const PAD = { top: 14, right: 12, bottom: 26, left: 34 }

const PLOT_WIDTH = WIDTH - PAD.left - PAD.right
const PLOT_HEIGHT = HEIGHT - PAD.top - PAD.bottom

const SERIES = [
  { key: 'capacity', color: colors.slateLight, labelKey: 'legend_capacity', dashed: true },
  { key: 'reserved', color: chartSeries[1], labelKey: 'legend_reservations', dashed: false },
  { key: 'used', color: chartSeries[2], labelKey: 'legend_occupancy', dashed: false },
] as const

const UsageOverTimeChart = ({ days }: { days: IDayTotals[] }) => {
  const { t, i18n } = useTranslation(['parking_pool_data'])

  const max = Math.max(...days.map(day => day.capacity), 1)
  const x = (index: number) =>
    PAD.left + (days.length > 1 ? (index * PLOT_WIDTH) / (days.length - 1) : PLOT_WIDTH / 2)
  const y = (value: number) => PAD.top + (1 - value / max) * PLOT_HEIGHT

  const line = (key: (typeof SERIES)[number]['key']) =>
    days.map((day, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(day[key]).toFixed(1)}`).join(' ')

  const usedArea = `${line('used')} L${x(days.length - 1).toFixed(1)},${y(0).toFixed(
    1,
  )} L${x(0).toFixed(1)},${y(0).toFixed(1)} Z`

  const gridValues = [0, Math.round(max / 2), max]
  const label = (day: string) =>
    dateIso2localeString(day, i18n.language, { month: '2-digit', day: '2-digit' })
  const axisIndexes = Array.from(
    new Set([0, Math.floor((days.length - 1) / 2), days.length - 1]),
  )

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: '16px', mb: '10px', flexWrap: 'wrap' }}>
        {SERIES.map(series => (
          <Box
            key={series.key}
            sx={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '12px', color: colors.textMuted, fontWeight: 600 }}
          >
            <Box
              component="span"
              sx={{
                width: '14px',
                height: series.dashed ? 0 : '3px',
                borderRadius: '2px',
                background: series.dashed ? 'transparent' : series.color,
                borderTop: series.dashed ? `2px dashed ${series.color}` : 'none',
              }}
            />
            {t(series.labelKey)}
          </Box>
        ))}
      </Box>

      <Box
        component="svg"
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        sx={{ width: '100%', height: 'auto', display: 'block', overflow: 'visible' }}
        role="img"
        aria-label={t('chart_usage_over_time')}
      >
        <defs>
          <linearGradient id="usedFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={chartSeries[2]} stopOpacity="0.28" />
            <stop offset="100%" stopColor={chartSeries[2]} stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {gridValues.map(value => (
          <g key={value}>
            <line
              x1={PAD.left}
              x2={WIDTH - PAD.right}
              y1={y(value)}
              y2={y(value)}
              stroke={colors.border}
              strokeWidth="1"
            />
            <text
              x={PAD.left - 8}
              y={y(value) + 4}
              textAnchor="end"
              fontSize="10"
              fontWeight="600"
              fill={colors.slate}
            >
              {value}
            </text>
          </g>
        ))}

        <path d={usedArea} fill="url(#usedFill)" stroke="none" />

        {SERIES.map(series => (
          <path
            key={series.key}
            d={line(series.key)}
            fill="none"
            stroke={series.color}
            strokeWidth={series.dashed ? 1.5 : 2.5}
            strokeDasharray={series.dashed ? '5 4' : undefined}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ))}

        {axisIndexes.map(index => (
          <text
            key={index}
            x={x(index)}
            y={HEIGHT - 8}
            textAnchor={index === 0 ? 'start' : index === days.length - 1 ? 'end' : 'middle'}
            fontSize="10"
            fontWeight="600"
            fill={colors.slate}
          >
            {label(days[index].day)}
          </text>
        ))}
      </Box>
    </Box>
  )
}

export default UsageOverTimeChart
